import { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, X, Search, Check, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';

// Multi-select dropdown over an array of users. Used to pick the
// notifyUserIds for a document or a contract — each picked user gets
// expiry-reminder emails (and an in-app pop-up) for that specific item.
//
// Props:
//   users:    [{ rowKey, displayName, email, isActive, ... }]
//   value:    string[] of user rowKeys (entraId)
//   onChange: (string[]) => void
//   label, placeholder, helpText, disabled
export default function UserMultiSelect({
  users = [],
  value = [],
  onChange,
  label,
  placeholder = 'Select notifiees...',
  helpText,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    function onDocClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    }
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const activeUsers = useMemo(
    () => (users || []).filter((u) => u.isActive !== false),
    [users]
  );

  const userById = useMemo(() => {
    const map = {};
    for (const u of activeUsers) map[u.rowKey] = u;
    return map;
  }, [activeUsers]);

  const filtered = useMemo(() => {
    if (!search) return activeUsers;
    const s = search.toLowerCase();
    return activeUsers.filter(
      (u) =>
        (u.displayName || '').toLowerCase().includes(s) ||
        (u.email || '').toLowerCase().includes(s)
    );
  }, [activeUsers, search]);

  const selectedSet = useMemo(() => new Set(value || []), [value]);

  function toggle(id) {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  }

  function remove(id) {
    onChange((value || []).filter((v) => v !== id));
  }

  return (
    <div className="flex flex-col gap-1.5" ref={wrapperRef}>
      {label && (
        <label className="text-[13px] font-medium text-muted-foreground flex items-center gap-1.5">
          <Bell className="h-3.5 w-3.5" />
          {label}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            'w-full flex flex-wrap items-center gap-1.5 rounded-lg border bg-card px-2 py-1.5 shadow-sm text-left transition-colors min-h-10',
            'border-border hover:border-primary/40 focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-ring/30',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {value.length === 0 && (
            <span className="text-sm text-muted-foreground/70 px-1.5">{placeholder}</span>
          )}
          {value.map((id) => {
            const u = userById[id];
            const label = u ? (u.displayName || u.email || id) : id;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                onClick={(e) => e.stopPropagation()}
              >
                {label}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); remove(id); }}
                  className="rounded p-0.5 text-primary/70 hover:bg-primary/20 hover:text-primary"
                  title={`Remove ${label}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
          <ChevronDown className="ml-auto h-4 w-4 text-muted-foreground shrink-0" />
        </button>

        {open && (
          <div className="absolute left-0 right-0 top-full mt-1 z-30 rounded-lg border border-border bg-card shadow-lg">
            <div className="border-b border-border p-2 flex items-center gap-2">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email..."
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
              />
              {value.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="max-h-[240px] overflow-y-auto py-1">
              {filtered.length === 0 && (
                <div className="text-sm text-muted-foreground p-3 text-center">
                  No matching users.
                </div>
              )}
              {filtered.map((u) => {
                const checked = selectedSet.has(u.rowKey);
                return (
                  <button
                    key={u.rowKey}
                    type="button"
                    onClick={() => toggle(u.rowKey)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent',
                      checked && 'bg-primary/5'
                    )}
                  >
                    <div
                      className={cn(
                        'h-4 w-4 rounded border flex items-center justify-center shrink-0',
                        checked ? 'border-primary bg-primary text-white' : 'border-border'
                      )}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium">{u.displayName || u.email || u.rowKey}</div>
                      {u.email && u.displayName && (
                        <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {helpText && (
        <span className="text-xs text-muted-foreground">{helpText}</span>
      )}
    </div>
  );
}
